<script setup lang="ts">
import Icon from '~/components/shared/Icon.vue';

import { CABINET_URL } from '~/constants/site/cabinet';

import type { INavigateItem } from '~/modules/tabBar/configs/tabBarConfig';

import { useTabBarSetup } from '~/modules/tabBar/uses/useTabBarSetup';
import { tabBarNavigateStore } from '~/modules/tabBar/stores/tabBarNavigateStore';
import { useTabBarNavigation } from '~/modules/tabBar/uses/useTabBarNavigation';
import { useTabBarRefElement } from '~/modules/tabBar/uses/useTabBarRefElement';
import { useVisitorStore } from '~/modules/visitors/stores/visitorStore';
import { useMenuCategoriesStore } from '~/modules/menu/stores/menuCategoriesStore';
import { useCartStore } from '~/modules/cart/stores/cartStore';

import {
  ETabBarSegments,
  BASIC_SEGMENT_DATA,
  NAVIGATE_ITEMS,
} from '~/modules/tabBar/configs/tabBarConfig';

interface Props {
  block?: boolean;
}

const props = defineProps<Props>();

useTabBarSetup();

const router = useRouter();
const { navigateTo } = useTabBarNavigation();
const { tabBarRef } = useTabBarRefElement();
const tabBarStore = tabBarNavigateStore();
const visitorStore = useVisitorStore();
const menuCategoriesStore = useMenuCategoriesStore();
const cartStore = useCartStore();

const segmentButton = (segment: ETabBarSegments) => {
  if (tabBarStore.activeSegment === segment) {
    tabBarStore.isRouteBlocked = true;
    tabBarStore.clearAllHistoryInSegmented(segment);
    const activeRoute = tabBarStore.getActiveRouteSegment();

    if (activeRoute.path === router.currentRoute.value.path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });

      /**
       * Активный модуль, временная реализация
       */
      if (activeRoute.path === BASIC_SEGMENT_DATA[ETabBarSegments.Catalog].path) {
        menuCategoriesStore.setActiveCategoriesListID(null);
        menuCategoriesStore.setActiveSecondaryItemID(null);
      }

    } else {
      navigateTo(activeRoute.path, activeRoute.scrollYTop);
    }

  } else {
    tabBarStore.isRouteBlocked = true;
    tabBarStore.addStepToHistory(tabBarStore.formattedRouteByIRouteItem(router.currentRoute.value.fullPath));
    tabBarStore.activeSegment = segment;
    const lastStep = tabBarStore.getLastStepFromActiveSegment();
    // const currentStep = tabBarStore.formattedRouteByIRouteItem(router.currentRoute.value.path);

    // if (lastStep && currentStep.segment === lastStep.segment) {
    //   Убрал отсюда
    //   tabBarStore.popLastPathFromActiveSegment();
    // }

    if (lastStep) {
      tabBarStore.popLastPathFromActiveSegment();
      navigateTo(lastStep.path, lastStep.scrollYTop);

    } else {
      const activeRouteSegment = tabBarStore.getActiveRouteSegment();
      navigateTo(activeRouteSegment.path, activeRouteSegment.scrollYTop);
    }
  }
};

const onItemClick = (item: INavigateItem) => {
  if (item.method) {
    item.method();
  }

  segmentButton(item.segment);
};

/**
 * Передача значения около иконки
 */
const navigationItems = computed(() => {
  return NAVIGATE_ITEMS.map((item) => {
    if (item.name === 'Избранное') {
      return { ...item, value: visitorStore.favoriteCount };
    }

    if (item.name === 'Корзина') {
      return { ...item, value: cartStore.quantity };
    }

    if (item.name === 'Профиль') {
      return { ...item };
    }

    return item;
  });
});
</script>

<template>
  <div ref="tabBarRef" class="tab-bar-navigate" :class="{ 'tab-bar-navigate_block': block }">
    <div class="tab-bar-navigate__inner">
      <div
        v-for="item in navigationItems"
        :key="item.segment"
        class="tab-bar-navigate__item"
        :class="{ 'tab-bar-navigate__item_active': tabBarStore.activeSegment === item.segment }"
        @click="onItemClick(item)"
      >
        <Icon :icon="item.icon" :class="'tab-bar-navigate-' + item.segment" :value="item.value"></Icon>
        <div class="tab-bar-navigate__item-text">{{ item.name }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tab-bar-navigate {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10010;
  width: 100%;
  max-width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $color-neutral-0;
  //padding-bottom: env(safe-area-inset-bottom, 0); // Для PWA

  &__inner {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    max-width: 100%;
    border-top: 1px solid $color-neutral-10;
  }

  &__item {
    cursor: pointer;
    color: $color-neutral-50;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;
    max-width: 100%;
    padding: 8px 0 9px 0;

    :deep(.icon__item) {
      background: $color-neutral-50;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: $color-neutral-10;
      }
    }

    &:active {
      //transform: scale(0.98);
      //background: $color-neutral-10;
    }

    &_active {
      color: $color-neutral-100;

      :deep(.icon__item) {
        background: $color-accent-1;
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background: $color-neutral-10;

          :deep(.icon__item) {
            background: $color-neutral-50;
          }
        }
      }
    }
  }

  &__item-text {
    //text-transform: uppercase;
    @include typography_template(8px, 10px, 0em);
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    flex-grow: 1;
  }

  &_block {
    user-select: none;
    pointer-events: none;
  }
}
</style>
